import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import ExplorerSection from 'pages/explorerPage/section/explorerSection';
import ExplorerFolder from 'pages/explorerPage/folder/explorerFolder';
import Breadcrumbs from 'shared/lib/components/controls/breadcrumbs/breadcrumbs';
import { useNavigate } from 'react-router-dom';
import ExplorerFile from 'pages/explorerPage/file/explorerFile';
import BreadcrumbsItem from 'shared/lib/components/controls/breadcrumbs/breadcrumbsItem';
import PageWrapper from 'shared/lib/components/containers/pageWrapper';
import ColumnCenterFlex from 'shared/lib/components/containers/columnCenterFlex';
import { Box } from '@mui/material';
import PagePaddingWrapper from 'shared/lib/components/containers/pagePaddingWrapper';
import ExplorerTable from 'pages/explorerPage/table/explorerTable';
import { useExplorerStore } from 'app/hooks/useExplorerStore';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { useTranslation } from 'react-i18next';
import MiniSearch from 'shared/lib/components/controls/search/miniSearch';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import SearchBlock from 'shared/lib/components/controls/search/searchBlock';
import { useEntityPath } from 'app/hooks/useEntityPath';
import { ExplorerEntityTypes } from 'entities/types';
import { map } from 'lodash-es';

export default observer(function ExplorerPage() {
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchUtils = useSearchParamsUtils();
  const { mobxStore } = useContext(MobxContext);

  const { getSectionSearchParams, getFolderSearchParams } = searchUtils;
  const { isLessLaptop } = useBooleanScreens();

  const currentEntity = useExplorerStore(mobxStore.explorerStore);

  const path = useEntityPath(
    currentEntity
      ? {
          id: currentEntity.model.id,
          type: currentEntity.type,
          name: currentEntity?.model.data?.name || '',
        }
      : null
  );

  const isSearch =
    currentEntity?.type === ExplorerEntityTypes.FOLDER ||
    currentEntity?.type === ExplorerEntityTypes.SECTION;

  return (
    <PageWrapper>
      {isLessLaptop && isSearch && <SearchBlock></SearchBlock>}
      <PagePaddingWrapper
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box>
          <Breadcrumbs>
            <BreadcrumbsItem
              onClick={() => {
                mobxStore.explorerStore.clearCurrentEntity();
                navigate('/', { replace: false });
              }}
              title={t('breadcrumbs.main')}
            >
              {t('breadcrumbs.main')}
            </BreadcrumbsItem>

            {path.sectionMeta ? (
              <BreadcrumbsItem
                key={path.sectionMeta.id}
                onClick={() => {
                  navigate({
                    search: getSectionSearchParams({
                      sectionId: path.sectionMeta!.id,
                    }),
                  });
                }}
                title={path.sectionMeta.name}
              >
                {path.sectionMeta.name}
              </BreadcrumbsItem>
            ) : null}

            {path.foldersMeta
              ? map(path.foldersMeta, (item) => (
                  <BreadcrumbsItem
                    key={item.id}
                    onClick={() => {
                      navigate({
                        search: getFolderSearchParams({
                          folderId: item.id,
                        }),
                      });
                    }}
                    title={item.name}
                  >
                    {item.name}
                  </BreadcrumbsItem>
                ))
              : null}

            {path.fileOrTableMeta ? (
              <BreadcrumbsItem title={path.fileOrTableMeta.name}>
                {path.fileOrTableMeta.name}
              </BreadcrumbsItem>
            ) : (
              ''
            )}
          </Breadcrumbs>
        </Box>

        <ColumnCenterFlex sx={{ flexGrow: 1 }}>
          {!isLessLaptop && isSearch && (
            <MiniSearch sx={{ pt: sp.s80 }}></MiniSearch>
          )}

          {currentEntity?.type === ExplorerEntityTypes.SECTION ? (
            <ExplorerSection
              sectionModel={currentEntity.model}
            ></ExplorerSection>
          ) : null}

          {currentEntity?.type === ExplorerEntityTypes.FILE ? (
            <ExplorerFile fileModel={currentEntity.model}></ExplorerFile>
          ) : null}

          {currentEntity?.type === ExplorerEntityTypes.TABLE ? (
            <ExplorerTable tableModel={currentEntity.model}></ExplorerTable>
          ) : null}

          {currentEntity?.type === ExplorerEntityTypes.FOLDER &&
          path.sectionMeta ? (
            <ExplorerFolder
              folderModel={currentEntity.model}
              sectionId={path.sectionMeta.id}
            ></ExplorerFolder>
          ) : null}
        </ColumnCenterFlex>
      </PagePaddingWrapper>
    </PageWrapper>
  );
});
